/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { InfoOutlined } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

import type { ProductFormMode, ProductFormValues } from './ProductDialog.types'
import {
  formatEnumLabel,
  PACKAGING_CONTAINER_OPTIONS,
  PACKAGING_UNIT_OPTIONS,
  stringAutocompleteFilter,
} from './productDialogUtils'

type ProductDialogFormFieldsProps = {
  mode: ProductFormMode
  saving: boolean
  values: ProductFormValues
  brandOptions: string[]
  categoryOptions: string[]
  commonNameOptions: string[]
  onSetField: (field: keyof ProductFormValues, value: string) => void
}

function ProductDialogFormFields({
  mode,
  saving,
  values,
  brandOptions,
  categoryOptions,
  commonNameOptions,
  onSetField,
}: ProductDialogFormFieldsProps) {
  const { t } = useT()

  return (
    <Stack spacing={2} sx={{ flex: 1, width: '100%' }}>
      <TextField
        label={t('correction.productDialog.upc', 'UPC')}
        value={values.upc}
        onChange={event => onSetField('upc', event.target.value)}
        disabled={mode === 'edit' || saving}
        fullWidth
        required={mode === 'create'}
        helperText={
          mode === 'create'
            ? t(
                'correction.productDialog.upcHelper',
                'Create requires a valid 12-digit UPC-A.',
              )
            : undefined
        }
      />
      <TextField
        label={t('correction.productDialog.name', 'Name')}
        value={values.name}
        onChange={event => onSetField('name', event.target.value)}
        disabled={saving}
        fullWidth
        required
        InputProps={{
          endAdornment: (
            <Tooltip
              title={t(
                'correction.productDialog.nameTooltip',
                'Use the full product name including packaging details. i.e. "Diet Coke 12 - 12oz Cans" for a 12 pack of Diet Coke.',
              )}
            >
              <IconButton
                size="small"
                aria-label={t(
                  'correction.productDialog.nameTooltip',
                  'Use the full product name including packaging details. i.e. "Diet Coke 12 - 12oz Cans" for a 12 pack of Diet Coke.',
                )}
                sx={{ color: 'text.secondary' }}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        }}
      />
      <Autocomplete<string, false, false, true>
        freeSolo
        options={commonNameOptions}
        filterOptions={stringAutocompleteFilter}
        value={values.commonName}
        inputValue={values.commonName}
        onChange={(_event, nextValue) =>
          onSetField('commonName', nextValue ?? '')
        }
        onInputChange={(_event, nextValue) =>
          onSetField('commonName', nextValue)
        }
        renderInput={params => (
          <TextField
            {...params}
            label={t('correction.productDialog.commonName', 'Common name')}
            disabled={saving}
            fullWidth
            required
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  <Tooltip
                    title={t(
                      'correction.productDialog.commonNameTooltip',
                      'Use the product name without packaging details. For example, "Diet Coke 12 - 12oz Cans" becomes just "Diet Coke".',
                    )}
                  >
                    <IconButton
                      size="small"
                      aria-label={t(
                        'correction.productDialog.commonNameTooltip',
                        'Use the product name without packaging details. For example, "Diet Coke 12 - 12oz Cans" becomes just "Diet Coke".',
                      )}
                      sx={{ color: 'text.secondary' }}
                    >
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Autocomplete<string, false, false, true>
          freeSolo
          options={brandOptions}
          filterOptions={stringAutocompleteFilter}
          sx={{ flex: 1 }}
          value={values.brand}
          inputValue={values.brand}
          onChange={(_event, nextValue) => onSetField('brand', nextValue ?? '')}
          onInputChange={(_event, nextValue) => onSetField('brand', nextValue)}
          renderInput={params => (
            <TextField
              {...params}
              label={t('correction.productDialog.brand', 'Brand')}
              disabled={saving}
              fullWidth
              required
            />
          )}
        />
      </Stack>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2">
            {t('correction.productDialog.packagingHeader', 'Packaging')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'correction.productDialog.packagingCaption',
              'Fill any packaging fields only if you want to save packaging details.',
            )}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('correction.productDialog.packagingQuantity', 'Quantity')}
            value={values.packagingQuantity}
            onChange={event =>
              onSetField('packagingQuantity', event.target.value)
            }
            disabled={saving}
            fullWidth
            type="number"
            required
          />
          <TextField
            label={t('correction.productDialog.packagingSize', 'Size')}
            value={values.packagingSize}
            onChange={event => onSetField('packagingSize', event.target.value)}
            disabled={saving}
            fullWidth
            type="number"
            required
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label={t('correction.productDialog.packagingUnit', 'Unit')}
            value={values.packagingUnit}
            onChange={event => onSetField('packagingUnit', event.target.value)}
            disabled={saving}
            fullWidth
            required
          >
            <MenuItem value="">{t('common.none', 'None')}</MenuItem>
            {PACKAGING_UNIT_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {formatEnumLabel(option)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t(
              'correction.productDialog.packagingContainer',
              'Container',
            )}
            value={values.packagingContainer}
            onChange={event =>
              onSetField('packagingContainer', event.target.value)
            }
            disabled={saving}
            fullWidth
            required
          >
            <MenuItem value="">{t('common.none', 'None')}</MenuItem>
            {PACKAGING_CONTAINER_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {formatEnumLabel(option)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default ProductDialogFormFields
